// Apolo IDE — useOutcomes hook (E1 outcomes-ledger, WS1.2).
//
// Records an agent_outcome at the end of a run (best-effort, fire-and-forget —
// a failed POST never blocks or breaks the UI; the run already happened) and
// reads the aggregated /summary with a 60s module-level cache + inflight dedup
// (same pattern as useBrainMCP). The summary is ModelTaskStat[] — never raw rows.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';

const TTL_MS = 60_000;
const summaryCache = new Map(); // key -> { value, expiresAt }
const inflight = new Map(); // key -> Promise

function keyFor(filters = {}) {
  return `${filters.task_type || ''}::${filters.model_id || ''}`;
}

function buildQuery(filters = {}) {
  const params = new URLSearchParams();
  if (filters.task_type) params.set('task_type', filters.task_type);
  if (filters.model_id) params.set('model_id', filters.model_id);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function clearOutcomesCache() {
  summaryCache.clear();
  inflight.clear();
}

/**
 * Record one outcome. Best-effort: resolves `{ ok, id? }` and never throws, so
 * callers can `void recordOutcome(...)` at run-end without a try/catch.
 *
 * @param {Record<string, unknown>} outcome
 * @returns {Promise<{ ok: boolean, id?: string, error?: string }>}
 */
export async function recordOutcome(outcome, { token } = {}) {
  try {
    const res = await apiFetch('/api/code-ide/outcomes', { method: 'POST', body: outcome, token });
    if (res.ok) return { ok: true, id: res.data?.id };
    return { ok: false, error: res.error || `HTTP ${res.status}` };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * Fetch the aggregated ModelTaskStat[] for the given filters, with a 60s cache
 * and inflight dedup. Only successful responses are cached (a transient error
 * returns [] without poisoning the cache).
 *
 * @param {{ task_type?: string, model_id?: string }} filters
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function fetchOutcomesSummary(filters = {}, { bypassCache = false, token } = {}) {
  const key = keyFor(filters);
  if (!bypassCache) {
    const hit = summaryCache.get(key);
    if (hit && hit.expiresAt > Date.now()) return hit.value;
    if (inflight.has(key)) return inflight.get(key);
  }
  const p = (async () => {
    try {
      const res = await apiFetch(`/api/code-ide/outcomes/summary${buildQuery(filters)}`, { token });
      if (res.ok && Array.isArray(res.data?.summary)) {
        const value = res.data.summary;
        summaryCache.set(key, { value, expiresAt: Date.now() + TTL_MS });
        return value;
      }
      return [];
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

export function useOutcomes(filters, { enabled = false } = {}) {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const refreshSummary = useCallback(async (bypassCache = false) => {
    setLoading(true);
    setError(null);
    try {
      const value = await fetchOutcomesSummary(filtersRef.current || {}, { bypassCache });
      setSummary(value);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refreshSummary(false);
    return undefined;
  }, [enabled, refreshSummary, JSON.stringify(filters || {})]); // eslint-disable-line react-hooks/exhaustive-deps

  return { summary, loading, error, recordOutcome, refreshSummary };
}
